import { useEffect, useRef, useState } from 'react';
import type { PlaceCandidate } from '@shared/types';
import { api, ApiError } from '../lib/api';

/**
 * Search Google Places and pick one. Attaching a place gives the card three
 * things at once: a real photo of the place, coordinates, and a Maps link.
 *
 * Dormant deployments (no GOOGLE_PLACES_API_KEY) get an explanatory message
 * and the paste-an-image-URL fallback instead of a dead search box.
 */
export function PlacePicker({
  initialQuery,
  bias,
  onPick,
  onCancel,
}: {
  initialQuery?: string;
  bias?: { lat: number; lng: number } | null;
  onPick: (place: PlaceCandidate, photoRef: string | null) => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState(initialQuery ?? '');
  const [results, setResults] = useState<PlaceCandidate[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [manualUrl, setManualUrl] = useState('');
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api
      .placesConfigured()
      .then((r) => setConfigured(r.configured))
      .catch(() => setConfigured(false));
    input.current?.focus();
  }, []);

  async function search(e?: React.FormEvent) {
    e?.preventDefault();
    if (query.trim().length < 2) return;
    setBusy(true);
    setError(null);
    try {
      const { results } = await api.searchPlaces(query.trim(), bias);
      setResults(results);
      if (!results.length) setError('No places matched that. Try a different name.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Search failed.');
    } finally {
      setBusy(false);
    }
  }

  /** The proxy URL carries the photo resource name we need to persist. */
  function photoRefOf(place: PlaceCandidate): string | null {
    if (!place.photoUrl) return null;
    const match = place.photoUrl.match(/[?&]ref=([^&]+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  }

  const manualCandidate = (): PlaceCandidate => ({
    placeId: '',
    name: '',
    address: null,
    lat: null,
    lng: null,
    mapUrl: '',
    photoUrl: manualUrl.trim(),
    rating: null,
    websiteUrl: null,
  });

  return (
    <div className="rounded-note border border-ink/12 bg-surface p-4">
      {configured !== false && (
        <>
          <div className="flex gap-2">
            <input
              ref={input}
              className="field"
              placeholder="Search a place — e.g. Secret Stash, Crested Butte"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void search();
                }
              }}
            />
            <button
              type="button"
              onClick={() => void search()}
              disabled={busy || query.trim().length < 2}
              className="btn-primary shrink-0 disabled:opacity-50"
            >
              {busy ? 'Searching…' : 'Search'}
            </button>
          </div>

          {error && <p className="mt-2 font-util text-meta text-muted">{error}</p>}

          {results.length > 0 && (
            <ul className="mt-3 space-y-1">
              {results.map((place) => (
                <li key={place.placeId}>
                  <button
                    type="button"
                    onClick={() => onPick(place, photoRefOf(place))}
                    className="flex w-full items-center gap-3 rounded-btn p-2 text-left
                               hover:bg-raised transition-colors"
                  >
                    {place.photoUrl ? (
                      <img
                        src={place.photoUrl}
                        alt=""
                        loading="lazy"
                        className="h-14 w-14 shrink-0 rounded-img object-cover bg-raised"
                      />
                    ) : (
                      <div className="h-14 w-14 shrink-0 rounded-img bg-raised" />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block font-util text-[0.9375rem] font-semibold text-ink">
                        {place.name}
                      </span>
                      {place.address && (
                        <span className="block truncate font-util text-meta text-muted">
                          {place.address}
                        </span>
                      )}
                    </span>
                    {place.rating && (
                      <span className="shrink-0 font-util text-meta text-muted">
                        {place.rating.toFixed(1)}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* Pasting a URL always stays available: place search never covers
          everything, and a deployment without a Places key needs it. */}
      <div className={configured !== false ? 'mt-4 border-t border-ink/10 pt-3' : undefined}>
        <label className="font-util text-meta text-muted">
          {configured === false
            ? 'Place search is not set up on this deployment. Paste an image URL instead.'
            : 'Or paste an image URL'}
        </label>
        <input
          className="field mt-1.5"
          placeholder="https://example.com/photo.jpg"
          value={manualUrl}
          onChange={(e) => setManualUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && manualUrl.trim()) {
              e.preventDefault();
              onPick(manualCandidate(), null);
            }
          }}
        />
        <div className="mt-2 flex gap-1">
          <button
            type="button"
            disabled={!manualUrl.trim()}
            onClick={() => onPick(manualCandidate(), null)}
            className="btn-primary !py-1.5 !text-meta disabled:opacity-50"
          >
            Use this image
          </button>
          <button type="button" onClick={onCancel} className="btn-quiet !py-1.5 !text-meta">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
